import { useEffect, useContext, useState } from 'react';
import { useToolsContext } from '../../../context/ToolsContext';
import { useGlobalVariablesContext } from '../../../context/GlobalVariablesContext';
import MapContext from '../../Map/MapContext';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import { findLayerByName } from '../../helpers/findLayerByName';

/**
 * Controlador "siempre montado" para la capa de Catastro.
 *
 * - Mantiene la capa activa aunque se cierre el sidebar o se cambie de pestaña.
 * - Sólo elimina la capa cuando `layerCadastro` pasa a false (botón "Desactivar") o cuando otra herramienta la desactiva.
 * - Mantiene el handler de click sobre el mapa mientras Catastro esté activo.
 */
export default function CatastroLayerController() {
  const { map } = useContext(MapContext);
  const { setToolState } = useToolsContext();
  const { layerCadastro, setPopUpPosition, setPopUpContent, setPopUpVisible } = useGlobalVariablesContext();

  const [capaCatastro, setCapaCatastro] = useState(null);

  const cambiaCursor = (tipo) => {
    if (!map) return;
    const viewport = map.getViewport();
    if (viewport) viewport.style.cursor = tipo;
  };

  const mostrarDatosCatastro = (evt) => {
    if (!map) return;
    // solo se mostraran los datos si la capa catastro está activa
    if (!findLayerByName('catastro', map)) return;
    if (!capaCatastro) return;

    // Convertir coordenadas geográficas a píxeles de pantalla
    const pixel = map.getPixelFromCoordinate(evt.coordinate);
    setPopUpPosition({ x: pixel[0], y: pixel[1] });
    setPopUpVisible(true);
    setPopUpContent(`<div class="catastro">
                        <h4>Catastro</h4>
                        <div class="loading-spinner">
                          <div class="spinner-border spinner-border-sm text-primary" role="status">
                            <span class="visually-hidden">Cargando...</span>
                          </div>
                          <span class="ms-2 small">Consultando datos...</span>
                        </div>
                      </div>`);

    const url = capaCatastro.getSource().getFeatureInfoUrl(
      evt.coordinate,
      map.getView().getResolution(),
      'EPSG:3857',
      { INFO_FORMAT: 'text/html' }
    );

    if (!url) return;

    fetch(url, { method: 'GET' })
      .then((response) => response.text())
      .then((responseText) => {
        // añadimos target blank delante de href para que abra referencia en otra ventana
        const contenidoPopUpNewTarget = responseText.replace(
          /href="/g,
          'target="_blank" rel="noopener noreferrer" href="'
        );

        const contenidoPopUp = `<div class="catastro">
                                  <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
                                    <h4 style="margin: 0;">Catastro</h4>
                                  </div>
                                  ${contenidoPopUpNewTarget}
                                 </div>`;

        setPopUpContent(contenidoPopUp);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log('Hubo un problema con la petición Fetch:' + err.message);
        // eslint-disable-next-line no-console
        console.log('Fetch Error', err);
        setPopUpContent(`<div class="catastro">
                          <h4>Catastro</h4>
                          <div class="error-message">
                            <p class="text-danger">Error al consultar los datos catastrales.</p>
                            <p class="small text-muted">Por favor, inténtelo de nuevo.</p>
                          </div>
                        </div>`);
      });
  };

  // Gestiona alta/baja de la capa en el mapa (NO depende del montaje del sidebar)
  useEffect(() => {
    if (!map) return;

    const existingLayer = findLayerByName('catastro', map);
    setCapaCatastro(existingLayer || null);

    if (layerCadastro === true) {
      // Si se activa catastro, desactivamos consulta de atributos (id real: "attributes")
      setToolState('attributes', { active: false });
      cambiaCursor('help');

      if (!existingLayer) {
        const catastro = new TileLayer({
          name: 'catastro',
          title: 'Servicio WMS Catastro',
          source: new TileWMS({
            url: 'https://ovc.catastro.meh.es/Cartografia/WMS/ServidorWMS.aspx?',
            crossOrigin: 'anonymous',
            params: {
              LAYERS: 'Catastro',
              TILED: 'true',
              VERSION: '1.1.1'
            }
          }),
          visible: true
        });
        map.addLayer(catastro);
        setCapaCatastro(catastro);
      }
    } else {
      // Desactivar: quitar capa del mapa
      if (existingLayer) {
        map.removeLayer(existingLayer);
      }
      setCapaCatastro(null);
      cambiaCursor('auto');
    }
  }, [map, layerCadastro, setToolState]);

  // Handler de click: activo mientras catastro esté activo
  useEffect(() => {
    if (!map) return;

    const mapClickHandlerCatastro = (event) => {
      mostrarDatosCatastro(event);
    };

    if (layerCadastro === true && capaCatastro) {
      map.on('click', mapClickHandlerCatastro);
    }

    return () => {
      map.un('click', mapClickHandlerCatastro);
    };
  }, [map, layerCadastro, capaCatastro]);

  return null;
}

